import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { User, LogOut, LayoutDashboard, Settings, Menu, X, MapPin, Sparkles } from 'lucide-react';
import { useState, useEffect } from 'react';

// Bombazoo Logo Icon - Premium style bomb
const BombLogo = () => (
  <svg viewBox="0 0 32 32" className="w-7 h-7" fill="none">
    <circle cx="16" cy="18" r="11" className="fill-current"/>
    <ellipse cx="13" cy="15" rx="3" ry="4" fill="white" fillOpacity="0.3"/>
    <path d="M16 7 Q19 4, 24 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="25" cy="2" r="3" fill="#f97316"/>
    <circle cx="25" cy="2" r="5" fill="#fbbf24" fillOpacity="0.4"/>
  </svg>
);

// Inline bomb emoji SVG for text
const BombEmoji = ({ className = "w-8 h-8", dark = false }) => (
  <svg viewBox="0 0 36 36" className={className} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }}>
    <circle cx="18" cy="22" r="13" fill={dark ? "#1e293b" : "#31373D"}/>
    <ellipse cx="14" cy="18" rx="3" ry="4" fill="white" fillOpacity="0.25"/>
    <path d="M18 9 Q22 5, 27 3" stroke={dark ? "#475569" : "#66757F"} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
    <circle cx="29" cy="4" r="3.5" fill="#F4900C"/>
    <circle cx="29" cy="4" r="5.5" fill="#FFCC4D" fillOpacity="0.5"/>
  </svg>
);

export const Navbar = () => {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-xl shadow-lg shadow-slate-200/50' 
          : 'bg-transparent'
      }`} 
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Solo bomba + texto */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="navbar-logo">
            <BombEmoji className={`w-10 h-10 transition-transform group-hover:scale-110 ${scrolled ? '' : 'drop-shadow-lg'}`} />
            <div className="flex flex-col">
              <span className={`text-2xl font-bold tracking-tight transition-colors ${
                scrolled ? 'text-slate-900' : 'text-white'
              }`}>
                Bombazoo
              </span>
              <span className={`text-[10px] tracking-[0.2em] uppercase font-medium transition-colors ${
                scrolled ? 'text-slate-400' : 'text-white/60'
              }`}>
                Viajes Exclusivos
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            <Link 
              to="/" 
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                scrolled 
                  ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-100' 
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
              data-testid="nav-home"
            >
              Inicio
            </Link>
            <Link 
              to="/certificados" 
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
                scrolled 
                  ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-100' 
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
              data-testid="nav-certificates"
            >
              <MapPin className="w-4 h-4" />
              Certificados
            </Link>
            <Link 
              to="/certificados" 
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
                scrolled 
                  ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50' 
                  : 'text-amber-300 hover:text-amber-200 hover:bg-amber-500/10'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              Ofertas
            </Link>
          </div>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center gap-3">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className={`flex items-center gap-2 rounded-full px-3 ${
                      scrolled ? '' : 'text-white hover:bg-white/10'
                    }`}
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center shadow-md">
                      {user?.profile_image ? (
                        <img src={user.profile_image} alt={user.name} className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <span className="text-white font-semibold text-sm">
                          {user?.name?.charAt(0)?.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <span className={`font-medium ${scrolled ? 'text-slate-700' : 'text-white'}`}>
                      {user?.name?.split(' ')[0]}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-xl shadow-xl border-slate-100">
                  <div className="px-4 py-3 bg-slate-50 rounded-t-xl">
                    <p className="text-sm font-semibold text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard" className="py-2.5">
                    <LayoutDashboard className="w-4 h-4 mr-2 text-teal-600" />
                    Mi Dashboard
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin" className="py-2.5">
                      <Settings className="w-4 h-4 mr-2 text-slate-500" />
                      Panel Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 py-2.5" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/auth')} 
                  className={`rounded-full ${scrolled ? '' : 'text-white hover:bg-white/10'}`}
                  data-testid="nav-login"
                >
                  Iniciar Sesión
                </Button>
                <Button 
                  onClick={() => navigate('/auth?mode=register')} 
                  className="rounded-full px-6 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 hover:-translate-y-0.5 transition-all"
                  data-testid="nav-register"
                >
                  Comenzar
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden p-2.5 rounded-xl transition-colors ${
              scrolled 
                ? 'text-slate-600 hover:bg-slate-100' 
                : 'text-white hover:bg-white/10'
            }`}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 bg-white rounded-2xl shadow-xl mt-2 border border-slate-100">
            <div className="flex flex-col gap-1 px-2">
              <Link 
                to="/" 
                className="px-4 py-3 rounded-xl text-slate-700 hover:bg-slate-50 font-medium" 
                onClick={() => setMobileMenuOpen(false)}
              >
                Inicio
              </Link>
              <Link 
                to="/certificados" 
                className="px-4 py-3 rounded-xl text-slate-700 hover:bg-slate-50 font-medium flex items-center gap-2" 
                onClick={() => setMobileMenuOpen(false)}
              >
                <MapPin className="w-4 h-4 text-teal-600" />
                Certificados
              </Link>
              <div className="h-px bg-slate-100 my-2" />
              {isAuthenticated ? (
                <>
                  <Link 
                    to="/dashboard" 
                    className="px-4 py-3 rounded-xl text-slate-700 hover:bg-slate-50 font-medium flex items-center gap-2" 
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <LayoutDashboard className="w-4 h-4 text-teal-600" />
                    Dashboard
                  </Link>
                  {isAdmin && (
                    <Link 
                      to="/admin" 
                      className="px-4 py-3 rounded-xl text-slate-700 hover:bg-slate-50 font-medium flex items-center gap-2" 
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Settings className="w-4 h-4 text-slate-500" />
                      Panel Admin
                    </Link>
                  )}
                  <button 
                    onClick={handleLogout} 
                    className="px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 font-medium flex items-center gap-2 text-left w-full"
                  >
                    <LogOut className="w-4 h-4" />
                    Cerrar Sesión
                  </button>
                </>
              ) : (
                <div className="flex flex-col gap-2 p-2">
                  <Button 
                    variant="outline" 
                    onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }} 
                    className="w-full rounded-xl"
                  >
                    Iniciar Sesión
                  </Button>
                  <Button 
                    onClick={() => { navigate('/auth?mode=register'); setMobileMenuOpen(false); }} 
                    className="w-full rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 text-white"
                  >
                    Comenzar
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
